import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'sonner';

import type {
  CreateBillingPeriodRequestDto,
  CurrentBillingPeriodUpdateRequestDto,
} from '../../../../backend/src/organisation/dto/organisation-billing-period-update.dto';
import { APIBaseChronos } from '../hosts';
import { QueryEntity } from '../types';
import useGetFetchConfig, { FetchMethod } from '../useGetFetchConfig';

interface UseUpdateOrganisationBillingPeriod {
  maxCredits?: number;
  billingStartDate: Date;
  billingEndDate: Date;
  seatCount?: number;
  defaultCreditLimit?: number;
}

export const useUpdateOrganisationBillingPeriod = (orgId: string) =>
  useBillingPeriodMutation<UseUpdateOrganisationBillingPeriod>(orgId, 'billing-period');

export const useUpdateCurrentBillingPeriod = (orgId: string) =>
  useBillingPeriodMutation<CurrentBillingPeriodUpdateRequestDto>(orgId, 'billing-period/current');

export const useCreateBillingPeriod = (orgId: string) =>
  useBillingPeriodMutation<CreateBillingPeriodRequestDto>(orgId, 'billing-period', FetchMethod.POST);

function useBillingPeriodMutation<T extends object>(orgId: string, path: string, method = FetchMethod.PATCH) {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: T) => {
      const fetchConfig = getFetchConfig({ method, data });
      const response = await fetch(`${APIBaseChronos}/api/org/${orgId}/${path}`, fetchConfig);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to update billing period');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries([QueryEntity.OrganisationById, { orgId }]);
      queryClient.invalidateQueries([QueryEntity.OrganisationBillingPlanHistory, { orgId }]);
      queryClient.invalidateQueries([QueryEntity.Subsets]);
      toast.success(method === FetchMethod.POST ? 'Billing period created' : 'Billing period updated');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
